import { Fragment, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { PageNavbar } from '@/pages/account';
import { useLayout } from '@/providers';
import { BerthListings } from './BerthListings';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { berthValidationSchema } from '@/utils/validations';
import { useCreateBerthMutation } from '@/services/BerthManagementService';
import { ErrorApiResponse } from '@/types/api.types';
import { IBerthData, PriceUnit } from '@/types/berth-management.types';
import { BerthModal } from './BerthModal';
import { usePermissions } from '@/hooks/usePermissions';
import { BerthType } from '@/constant';

const BerthManagement = () => {
  const { currentLayout } = useLayout();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<IBerthData>>({
    harbor_id: '',
    category_id: '',
    berth_type: undefined,
    amenities: [],
    price: undefined,
    price_unit: PriceUnit.PER_DAY,
    status: 1,
    harbor: { id: '', name: '' },
    pier_prefix: '',
    pier_id: '',
    mooring_style: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();
  const [createBerth, { isLoading: isCreating }] = useCreateBerthMutation();
  const { hasPermission } = usePermissions();

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setFormData({
      harbor_id: '',
      category_id: '',
      berth_type: undefined,
      amenities: [],
      price: undefined,
      price_unit: PriceUnit.PER_DAY,
      status: 1,
      harbor: { id: '', name: '' },
      pier_id: '',
      pier_prefix: '',
      mooring_style: '',
    });
    setErrors({});
  };

  const handleModalClose = () => {
    setIsCreateModalOpen(false);
    setFormData({});
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      await berthValidationSchema.validate(formData, { abortEarly: false });
      const seasonalPricing = (
        formData as unknown as {
          seasonal_pricing?: Partial<Record<string, number>>;
        }
      )?.seasonal_pricing;

      const data: Partial<IBerthData> = {
        harbor_id: formData.harbor_id || '',
        category_id: formData.category_id || '',
        berth_type:
          formData.berth_type === undefined || formData.berth_type === null
            ? undefined
            : Number(formData.berth_type),
        amenities: formData.amenities || [],
        ...(Number(formData.berth_type) === BerthType.SEASONAL
          ? {
              seasonal_pricing: seasonalPricing,
            }
          : {
              price: Number(formData.price) || 0,
              price_unit: formData.price_unit as PriceUnit,
            }),
        status: Number(formData.status) || 1,
        count: Number(formData.quantity) || 1,
        pier_id: formData.pier_id,
        pier_prefix: formData.pier_prefix,
        mooring_style: formData.mooring_style,
      };
      await createBerth(data).unwrap();

      handleModalClose();
      enqueueSnackbar('Berth created  1234', { variant: 'success' });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      } else {
        const errorMessage =
          (err as ErrorApiResponse)?.data?.message || 'Failed to create berth';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        console.error('createBerth error:', err);
      }
      throw err;
    }
  };

  return (
    <Fragment>
      <PageNavbar />

      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle />
              <ToolbarDescription>
                You can manage your berth inventory here.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {hasPermission('berth_management_create') && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreateModal}
                  aria-label="Add new berth"
                >
                  Add New Berth
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}
      <Container>
        <BerthListings />
      </Container>
      <BerthModal
        open={isCreateModalOpen}
        onClose={handleModalClose}
        mode="create"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isCreating}
      />
    </Fragment>
  );
};

export { BerthManagement };
